import { type User } from "@microsoft/microsoft-graph-types";
import { type UserInfo, getUserInfo, searchMarketCoworkers } from "@repo/api";
import { Loading, Text, personIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  getAbbreviationIndex,
  hasItems,
  isDefined,
  maskEmail,
} from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect, useRef, useState } from "react";

import { useOrderedCustomerMeetingPoints } from "../../../hooks/useCMP";
import { useOnClickOutside } from "../../../hooks/useOnClickOutside";
import { useSettings } from "../../../settings/SettingsProvider";
import { CoWorkerName } from "../../CoWorkerName";
import { DebouncedSearchField } from "../../DebouncedSearchField";
import { SectionContainer } from "../../SectionContainer";
import {
  type SelectListItem,
  SelectListView,
  type SelectListViewProps,
} from "../../SelectListView";
import { useToast } from "../../Toast";

export interface SearchCoworkerProps {
  value?: string;
  selectedCoworkers?: UserInfo[];
  setIsActivated?: (activated: boolean) => void;
  onSelectCoworker: (selectedUsers: UserInfo[]) => void;
  canSelectMultipleCoworkers?: boolean;
}

const SearchCoworkerResult = ({
  searchResult,
  selectedCoworkers,
  canSelectMultipleCoworkers,
  onChange,
}: {
  searchResult: User[];
  selectedCoworkers: UserInfo[] | undefined;
  canSelectMultipleCoworkers?: boolean;
  onChange: SelectListViewProps["onChange"];
}) => {
  const { customerMode } = useSettings();
  const list: SelectListItem[] = searchResult.map((el) => ({
    value: el.mail ?? "",
    name: el.mail ?? "",
    title: (
      <CoWorkerName
        email={el.mail ?? ""}
        textStyle="Heading.XS"
        emails={searchResult.map((u) => u.mail ?? "")}
      />
    ),
    description: customerMode
      ? maskEmail(
          el.mail ?? "",
          getAbbreviationIndex(
            el.mail ?? "",
            searchResult.map((u) => u.mail ?? "")
          )
        )
      : (el.mail ?? ""),
    icon: personIcon,
    disabled: isEmpty(el.mail),
  }));

  return (
    <SelectListView
      id="search-coworker-result"
      list={list}
      onChange={onChange}
      selectedKeys={
        isDefined(selectedCoworkers)
          ? selectedCoworkers.map((el) => el.emailId)
          : []
      }
      selectionMode={canSelectMultipleCoworkers ? "MULTI" : "SINGLE"}
    />
  );
};

export const SearchCoworker = ({
  value,
  selectedCoworkers,
  setIsActivated,
  onSelectCoworker,
  canSelectMultipleCoworkers,
}: SearchCoworkerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState<string>(value ?? "");
  const [isSearching, setSearching] = useState<boolean>(false);
  const [searchResult, setSearchResult] = useState<User[] | undefined>(
    undefined
  );
  const { data: cmps } = useOrderedCustomerMeetingPoints();
  const { updateToast } = useToast();

  useOnClickOutside(ref, () => {
    setSearching(false);
    setSearchResult(undefined);
  });

  // Clear search result when user clears input field
  useEffect(() => {
    if (searchValue === "") {
      setSearching(false);
      setSearchResult(undefined);
    }
  }, [searchValue, searchResult]);

  const runSearch = useCallback(async (term: string) => {
    try {
      if (term !== "" && term !== undefined) {
        const users = await searchMarketCoworkers(term);
        setSearchResult(users);

        setIsActivated?.(users !== undefined);
      } else {
        setIsActivated?.(false);
      }
    } catch {
      updateToast({
        title: t("prompt.error_when_searching"),
      });
    } finally {
      setSearching(false);
    }
    // TODO: revisit and fix
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Run the search when searchValue has been changed
  useEffect(() => {
    runSearch(searchValue);
  }, [runSearch, searchValue]);

  // Called when user changes values in the field
  const handleChange = (term: string) => {
    if (term !== searchValue) {
      setSearchValue(term);
      setSearching(true);
      setIsActivated?.(true);
    }
  };

  const handleCoworkerSelect = (checked: boolean, item: SelectListItem) => {
    if (!hasItems(searchResult)) {
      return;
    }

    let newList: UserInfo[];
    if (checked) {
      const user = searchResult.find((u) => u.mail === item.value);
      if (!user) {
        return;
      }
      newList = [...(selectedCoworkers ?? []), getUserInfo(user, cmps)];
    } else {
      newList = (selectedCoworkers ?? []).filter(
        (c) => c.emailId !== item.value
      );
    }

    onSelectCoworker(newList);
  };

  return (
    <SectionContainer>
      <DebouncedSearchField
        id="search-coworker-input"
        placeholder={t("action.placeholder_search")}
        value={searchValue}
        onChange={(term) => handleChange(term)}
      />

      {isSearching && <Loading />}
      {hasItems(searchResult) && (
        <SearchCoworkerResult
          searchResult={searchResult}
          onChange={handleCoworkerSelect}
          selectedCoworkers={selectedCoworkers}
          canSelectMultipleCoworkers={canSelectMultipleCoworkers}
        />
      )}
      {!hasItems(searchResult) && searchValue && !isSearching && (
        <Text>{t("prompt.no_result_for", { searchTerm: searchValue })}</Text>
      )}
    </SectionContainer>
  );
};
